import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff, MapPin, DollarSign, Clock, Save, X, Users, User, Phone, Mail, Hash, Type, Settings, Lock, Layout, Link as LinkIcon, Code, ExternalLink, Copy, Upload, Archive, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

const AdminJobs = () => {
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' or 'users'
  const [viewUser, setViewUser] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    experience: '',
    salary: '',
    location: '',
    remote: false,
    country: '',
    type: 'Full-time',
    description: '',
    requirements: []
  });

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [passwordResetStatus, setPasswordResetStatus] = useState('');
  
  // Password Visibility States
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [showProfilePassword, setShowProfilePassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Landing Page Management State
  const [landingPages, setLandingPages] = useState([]);
  const [showLandingPageForm, setShowLandingPageForm] = useState(false);
  const [editingLandingPage, setEditingLandingPage] = useState(null);
  const [landingPageFormData, setLandingPageFormData] = useState({
    slug: '',
    title: '',
    description: '',
    html_content: '',
    is_active: true,
    valid_till: ''
  });
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [editingLandingPageName, setEditingLandingPageName] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    secondary_email: '',
    phone: '',
    username: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [smtpSettings, setSmtpSettings] = useState({
    server: '',
    port: 587,
    sender_email: '',
    password: '',
    recipients: '',
    use_tls: true,
    has_password: false
  });

  // Check for reset token in URL on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setIsResetPasswordOpen(true);
      // Clean URL
      window.history.replaceState({}, document.title, "/admin");
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await http.get('/admin/me');
        if (!mounted) return;
        setIsAuthenticated(true);
        setCurrentUser(response.data);
      } catch (e) {
        if (!mounted) return;
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        if (!mounted) return;
        setAuthChecked(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      setProfileData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        secondary_email: currentUser.secondary_email || '',
        phone: currentUser.phone || '',
        username: currentUser.username || ''
      });
    }
  }, [isAuthenticated, currentUser]);

  // User management state
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    secondary_email: '',
    phone: '',
    role: 'Recruiter',
    modules: ['jobs'],
    is_active: true
  });

  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadFilters, setLeadFilters] = useState({ 
    source: 'all', 
    openOnly: true, 
    search: '',
    interested: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    phone: '',
    country_code: '',
    email: '',
    professional: '',
    location: '',
    company: '',
    reach_time: '',
    reach_timezone: '',
    comments_1: '',
    comments_2: '',
    is_open: true,
    interested: null
  });
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Admin logs state
  const [adminLogs, setAdminLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [logFilters, setLogFilters] = useState({
    userId: ''
  });

  const fetchAdminLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      setLogsError('');
      const params = {};
      if (logFilters.userId) {
        params.user_id = logFilters.userId;
      }
      const response = await http.get('/admin/logs', { params });
      setAdminLogs(response.data || []);
    } catch (error) {
      console.error('Error fetching admin logs:', error);
      setLogsError(error?.response?.data?.detail || error?.message || 'Failed to load activity logs');
      setAdminLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [logFilters.userId]);

  const formatLogTimestamp = useCallback((value) => {
    if (!value) return '';
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    const normalized = String(value).trim().replace(' ', 'T');
    const d2 = new Date(normalized);
    if (!Number.isNaN(d2.getTime())) return d2.toLocaleString();
    return String(value);
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      setLeadsLoading(true);
      const response = await http.get(`/admin/leads`, {
        params: { 
          source: leadFilters.source,
          search: leadFilters.search,
          interested: leadFilters.interested,
          dateFrom: leadFilters.dateFrom,
          dateTo: leadFilters.dateTo
        }
      });
      setLeads(response.data || []);
      // Clear selections when fetching new data
      setSelectedLeads(new Set());
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  }, [leadFilters]);

  const getDefaultModulesForRole = useCallback((role) => {
    const roleNorm = (role || '').toLowerCase();
    if (roleNorm === 'super admin') return ['jobs', 'landing_pages', 'users', 'leads', 'logs'];
    if (roleNorm === 'hr manager') return ['jobs', 'leads', 'logs'];
    if (roleNorm === 'tech lead') return ['jobs', 'logs'];
    return ['jobs', 'logs'];
  }, []);

  const getEffectiveModules = useCallback((user) => {
    if (!user) return [];
    const mods = Array.isArray(user.modules) ? user.modules : [];
    return mods.length ? mods : getDefaultModulesForRole(user.role);
  }, [getDefaultModulesForRole]);

  const hasModule = useCallback((moduleName) => getEffectiveModules(currentUser).includes(moduleName), [currentUser, getEffectiveModules]);
  const isSuperAdmin = currentUser?.role === 'Super Admin';
  const isViewingUser = isSuperAdmin && !!viewUser;
  const currentUserEmail = String(currentUser?.email || '').toLowerCase();
  const viewUserEmail = String(viewUser?.email || '').toLowerCase();
  const jobsForDisplay = isViewingUser
    ? jobs.filter(j => String(j?.recruiter?.email || '').toLowerCase() === viewUserEmail)
    : (isSuperAdmin
      ? jobs
      : jobs.filter(j => String(j?.recruiter?.email || '').toLowerCase() === currentUserEmail));
  const landingPagesForDisplay = isViewingUser
    ? landingPages.filter(p => String(p?.created_by_email || '').toLowerCase() === viewUserEmail)
    : (isSuperAdmin
      ? landingPages
      : landingPages.filter(p => String(p?.created_by_email || '').toLowerCase() === currentUserEmail));

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tab = (params.get('tab') || '').trim().toLowerCase();
      if (!tab) return;

      const mods = getEffectiveModules(currentUser);
      const allowedTabs = new Set(['jobs', 'users', 'landing_pages', 'leads', 'logs', 'typography', 'profile']);
      if (!allowedTabs.has(tab)) return;

      if (tab === 'users' && !mods.includes('users')) return;
      if (tab === 'landing_pages' && !mods.includes('landing_pages')) return;
      if (tab === 'leads' && !mods.includes('leads')) return;
      if (tab === 'logs' && !mods.includes('logs')) return;

      setActiveTab(tab);
      if (tab === 'leads') fetchLeads();
      if (tab === 'logs') fetchAdminLogs();
    } catch (e) {
      // ignore
    }
  }, [location.search, currentUser, getEffectiveModules, fetchLeads, fetchAdminLogs]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchJobs();
      const mods = getEffectiveModules(currentUser);
      if (mods.includes('users')) fetchUsers();
      if (mods.includes('landing_pages')) fetchLandingPages();
      if (mods.includes('leads')) fetchLeads();
    }
  }, [isAuthenticated, currentUser, getEffectiveModules, fetchLeads]);

  const handleLeadSelect = (leadId) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeads(new Set());
      setSelectAll(false);
    } else {
      const visibleLeadIds = leads
        .filter(lead => leadFilters.openOnly ? lead.is_open !== false : true)
        .map(lead => `${lead.source}-${lead.id}`);
      setSelectedLeads(new Set(visibleLeadIds));
      setSelectAll(true);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    setLeadsLoading(true);
    try {
      const [source, id] = leadId.split('-');
      await http.put(`/admin/leads/${source}/${id}/status`, { status: newStatus });
      await fetchLeads();
      alert(`Lead status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Error updating lead status. Please try again.');
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleBulkClose = async () => {
    if (selectedLeads.size === 0) {
      alert('Please select at least one lead to close');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to close ${selectedLeads.size} selected lead(s)?`)) {
      return;
    }

    setLeadsLoading(true);
    try {
      const updatePromises = Array.from(selectedLeads).map(leadId => {
        const [source, id] = leadId.split('-');
        return http.put(`/admin/leads/${source}/${id}/status`, { status: 'closed' });
      });
      
      await Promise.all(updatePromises);
      await fetchLeads();
      alert(`Successfully closed ${selectedLeads.size} lead(s)`);
    } catch (error) {
      console.error('Error closing leads:', error);
      alert('Error closing some leads. Please try again.');
    } finally {
      setLeadsLoading(false);
    }
  };

  const exportToExcel = () => {
    const visibleLeads = leads.filter(lead => {
      if (leadFilters.openOnly && lead.is_open === false) return false;
      if (selectedLeads.size > 0 && !selectedLeads.has(`${lead.source}-${lead.id}`)) return false;
      return true;
    });

    if (visibleLeads.length === 0) {
      alert('No leads to export');
      return;
    }

    const csvContent = [
      ['Name', 'Email', 'Phone', 'Company', 'Location', 'Service', 'Interested', 'Status', 'Source', 'Created'],
      ...visibleLeads.map(lead => [
        lead.name || '',
        lead.email || '',
        lead.phone || '',
        lead.company || '',
        lead.location || '',
        lead.service || '',
        lead.interested === true ? 'Yes' : lead.interested === false ? 'No' : 'Unknown',
        lead.is_open === false ? 'Closed' : 'Open',
        `${lead.source}${lead.slug ? ` - ${lead.slug}` : ''}`,
        new Date(lead.created_at).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openLeadEditor = (lead) => {
    setEditingLead(lead);
    setLeadFormData({
      name: lead?.name || '',
      phone: lead?.phone || '',
      country_code: lead?.country_code || '',
      email: lead?.email || '',
      professional: lead?.professional || '',
      location: lead?.location || '',
      company: lead?.company || '',
      reach_time: lead?.reach_time || '',
      reach_timezone: lead?.reach_timezone || '',
      comments_1: lead?.comments_1 || '',
      comments_2: lead?.comments_2 || '',
      is_open: lead?.is_open !== false,
      interested: typeof lead?.interested === 'boolean' ? lead.interested : null
    });
    setShowLeadForm(true);
  };

  const handleLeadInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'interested') {
      if (value === 'yes') setLeadFormData(prev => ({ ...prev, interested: true }));
      else if (value === 'no') setLeadFormData(prev => ({ ...prev, interested: false }));
      else setLeadFormData(prev => ({ ...prev, interested: null }));
      return;
    }
    setLeadFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!editingLead) return;
    setLeadsLoading(true);
    try {
      await http.put(`/admin/leads/${editingLead.source}/${editingLead.id}`, leadFormData);
      await fetchLeads();
      setShowLeadForm(false);
      setEditingLead(null);
    } catch (error) {
      console.error('Error saving lead:', error);
      alert(error.response?.data?.detail || 'Error saving lead. Please try again.');
    } finally {
      setLeadsLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await http.get(`/jobs`, { params: { active_only: false } });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await http.get(`/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const response = await http.get(`/admin/smtp-settings`);
      const data = response.data || {};
      setSmtpSettings(prev => ({
        ...prev,
        server: data.server || '',
        port: data.port || 587,
        sender_email: data.sender_email || '',
        recipients: data.recipients || '',
        use_tls: data.use_tls !== false,
        has_password: !!data.has_password,
        password: ''
      }));
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
      alert(error.response?.data?.detail || 'Error fetching SMTP settings');
    }
  };

  const handleSmtpSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        server: smtpSettings.server,
        port: Number(smtpSettings.port),
        sender_email: smtpSettings.sender_email,
        recipients: smtpSettings.recipients,
        use_tls: !!smtpSettings.use_tls,
        password: smtpSettings.password
      };
      const response = await http.put(`/admin/smtp-settings`, payload);
      const data = response.data || {};
      setSmtpSettings(prev => ({
        ...prev,
        server: data.server || prev.server,
        port: data.port || prev.port,
        sender_email: data.sender_email || prev.sender_email,
        recipients: data.recipients || prev.recipients,
        use_tls: data.use_tls !== false,
        has_password: !!data.has_password,
        password: ''
      }));
      alert('SMTP settings updated successfully!');
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      alert(error.response?.data?.detail || 'Error saving SMTP settings');
    }
  };

  const canEditJob = (job) => {
    if (isViewingUser) return false;
    // Super Admin and HR Manager can edit any job
    if (['Super Admin', 'HR Manager'].includes(currentUser?.role)) {
      return true;
    }
    const currentEmail = String(currentUser?.email || '').toLowerCase();
    const jobEmail = String(job?.recruiter?.email || '').toLowerCase();
    return !!currentEmail && currentEmail === jobEmail;
  };

  const canDeleteJob = (job) => {
    if (isViewingUser) return false;
    // Only Super Admin and HR Manager can delete jobs
    return ['Super Admin', 'HR Manager'].includes(currentUser?.role);
  };

  const fetchLandingPages = async () => {
    try {
      const response = await http.get(`/admin/landing-pages`);
      setLandingPages(response.data);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
      setLandingPages([]);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await http.post(`/admin/login`, loginData);
      if (response.data.success) {
        setIsAuthenticated(true);
        setCurrentUser(response.data.user);
        setAuthChecked(true);
      } else {
        setLoginError(response.data.message || 'Invalid credentials');
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await http.post(`/admin/forgot-password`, { email: forgotPasswordEmail });
      alert(`Password reset link sent to ${forgotPasswordEmail}`);
      setIsForgotPasswordOpen(false);
      // Simulate receiving token (in real app, this comes from email link)
      // For demo, we'll auto-open reset modal
      // const token = response.data.reset_token; 
      // setResetToken(token);
      // setIsResetPasswordOpen(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error sending reset email');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
        setPasswordResetStatus('Passwords do not match');
        return;
    }

    try {
      await http.post(`/admin/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      alert('Password reset successful! Please login with new password.');
      setIsResetPasswordOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordResetStatus('');
    } catch (error) {
        setPasswordResetStatus(error.response?.data?.detail || 'Error resetting password');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRequirementsChange = (e) => {
    const requirements = e.target.value.split('\n').filter(req => req.trim());
    setFormData(prev => ({
      ...prev,
      requirements
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Add recruiter info from current user
    const jobData = {
      ...formData,
      recruiter: {
        user_id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        phone: currentUser.phone
      }
    };
    
    try {
      if (editingJob) {
        const response = await http.put(`/jobs/${editingJob.id}`, jobData);
        setJobs(prev => prev.map(job => 
          job.id === editingJob.id ? response.data : job
        ));
      } else {
        const response = await http.post(`/jobs`, jobData);
        setJobs(prev => [response.data, ...prev]);
      }
      
      setFormData({
        title: '',
        experience: '',
        salary: '',
        location: '',
        remote: false,
        country: '',
        type: 'Full-time',
        description: '',
        requirements: []
      });
      setShowForm(false);
      setEditingJob(null);
    } catch (error) {
      console.error('Error saving job:', error);
      alert('Error saving job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (job) => {
    setFormData({
      title: job.title,
      experience: job.experience,
      salary: job.salary,
      location: job.location,
      remote: job.remote,
      country: job.country,
      type: job.type,
      description: job.description,
      requirements: job.requirements || []
    });
    setEditingJob(job);
    setShowForm(true);
  };

  const handleDelete = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await http.delete(`/jobs/${jobId}`);
        setJobs(prev => prev.filter(job => job.id !== jobId));
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Error deleting job. Please try again.');
      }
    }
  };

  // User Management Functions
  const handleUserInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (name === 'role') {
      setUserFormData(prev => ({
        ...prev,
        role: value,
        modules: prev.modules?.length ? prev.modules : getDefaultModulesForRole(value)
      }));
      return;
    }
    if (name === 'email' || name === 'secondary_email') {
      setUserFormData(prev => ({ ...prev, [name]: String(value || '').trim().toLowerCase() }));
      return;
    }
    setUserFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleUserModuleToggle = (moduleName) => {
    setUserFormData(prev => {
      const current = Array.isArray(prev.modules) ? prev.modules : [];
      if (current.includes(moduleName)) {
        return { ...prev, modules: current.filter(m => m !== moduleName) };
      }
      return { ...prev, modules: [...current, moduleName] };
    });
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    const primaryEmail = String(userFormData.email || '').trim().toLowerCase();
    if (!primaryEmail.endsWith('@tripodtechnologies.com')) {
      alert('Primary email must be a @tripodtechnologies.com address');
      return;
    }
    setLoading(true);
    
    try {
      const payload = { ...userFormData };
      if (!payload.secondary_email || String(payload.secondary_email).trim() === '') {
        delete payload.secondary_email;
      }
      if (editingUser && (!payload.password || String(payload.password).trim() === '')) {
        delete payload.password;
      }
      if (editingUser) {
        await http.put(`/admin/users/${editingUser.id}`, payload);
      } else {
        await http.post(`/admin/users`, payload);
      }
      
      fetchUsers();
      setUserFormData({
        username: '',
        password: '',
        name: '',
        email: '',
        secondary_email: '',
        phone: '',
        role: 'Recruiter',
        modules: ['jobs'],
        is_active: true
      });
      setShowUserForm(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map(d => d?.msg || d?.message || JSON.stringify(d)).join('\n')
        : detail;
      alert(message || 'Error saving user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setUserFormData({
      username: user.username,
      password: '',
      name: user.name,
      email: user.email,
      secondary_email: user.secondary_email || '',
      phone: user.phone,
      role: user.role,
      modules: Array.isArray(user.modules) && user.modules.length ? user.modules : getDefaultModulesForRole(user.role),
      is_active: user.is_active !== false
    });
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await http.delete(`/admin/users/${userId}`);
        if (viewUser?.id === userId) {
          setViewUser(null);
        }
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error.response?.data?.detail || 'Error deleting user. Please try again.');
      }
    }
  };

  // Landing Page Management Functions
  const handleLandingPageInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLandingPageFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLandingPageFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'text/html') {
        alert('Please upload an HTML file');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setLandingPageFormData(prev => ({
          ...prev,
          html_content: event.target.result
        }));
      };
      reader.readAsText(file);
    }
  };

  const [importUrl, setImportUrl] = useState('');

  const handleLandingPageUrlUpload = async () => {
    if (!importUrl) {
      alert("Please enter a URL first");
      return;
    }
    
    // Basic URL validation
    try {
      new URL(importUrl);
    } catch (e) {
      alert("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    setLoading(true);
    
    try {
      // Use a CORS proxy service for demo purposes
      // In production, this should be handled by your own backend
      const proxyUrl = 'https://api.allorigins.win/raw?url=' + encodeURIComponent(importUrl);
      
      const response = await fetch(proxyUrl);
      if (!response.ok) throw new Error('Failed to fetch');
      const html = await response.text();
      
      if (!html) throw new Error('Empty response');
  
      setLandingPageFormData(prev => ({
        ...prev,
        html_content: html
      }));
      alert("HTML fetched successfully!");
      setImportUrl(''); // Clear input after success
    } catch (error) {
      console.error("Fetch error:", error);
      alert("Could not fetch URL. The website might be blocking access or is unavailable.\n\nPlease try copy-pasting the source code instead.");
    } finally {
      setLoading(false);
    }
  };

  const handleLandingPageSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingLandingPage) {
        await http.put(`/admin/landing-pages/${editingLandingPage.id}`, landingPageFormData);
      } else {
        await http.post(`/admin/landing-pages`, {
          ...landingPageFormData,
          created_by_email: currentUser?.email
        });
      }

      await fetchLandingPages();
      
      setLandingPageFormData({
        slug: '',
        title: '',
        description: '',
        html_content: '',
        is_active: true,
        valid_till: ''
      });
      setShowLandingPageForm(false);
      setEditingLandingPage(null);
      alert('Landing page saved successfully!');
    } catch (error) {
      console.error('Error saving landing page:', error);
      alert(error.response?.data?.detail || 'Error saving landing page');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLandingPage = (page) => {
    setLandingPageFormData({
      slug: page.slug,
      title: page.title,
      description: page.description || '',
      html_content: page.html_content,
      is_active: page.is_active,
      valid_till: page.valid_till || ''
    });
    setEditingLandingPage(page);
    setShowLandingPageForm(true);
  };

  const toggleLandingPageStatus = async (page) => {
    try {
      await http.post(`/admin/landing-pages/${page.id}/toggle`);
      await fetchLandingPages();
    } catch (error) {
      console.error('Error updating landing page status:', error);
      alert('Error updating landing page status');
    }
  };

  const startEditingName = (page) => {
    setEditingLandingPageName(page.id);
    setEditingNameValue(page.title);
  };

  const saveLandingPageName = async (page) => {
    if (!editingNameValue.trim()) {
      alert('Page title cannot be empty');
      return;
    }
    try {
      const updatedPage = { ...page, title: editingNameValue.trim() };
      await http.put(`/admin/landing-pages/${page.id}`, updatedPage);
      await fetchLandingPages();
      setEditingLandingPageName(null);
      setEditingNameValue('');
    } catch (error) {
      console.error('Error updating landing page name:', error);
      alert('Error updating landing page name');
    }
  };

  const cancelEditingName = () => {
    setEditingLandingPageName(null);
    setEditingNameValue('');
  };

  const handleDeleteLandingPage = async (pageId) => {
    if (window.confirm('Are you sure you want to delete this landing page?')) {
      try {
        await http.delete(`/admin/landing-pages/${pageId}`);
        await fetchLandingPages();
      } catch (error) {
        console.error('Error deleting landing page:', error);
        alert(error.response?.data?.detail || 'Error deleting landing page');
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }, (err) => {
      console.error('Could not copy text: ', err);
    });
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const primaryEmail = String(profileData.email || '').trim().toLowerCase();
    if (primaryEmail && !primaryEmail.endsWith('@tripodtechnologies.com')) {
      alert('Primary email must be a @tripodtechnologies.com address');
      return;
    }
    try {
      const response = await http.put(`/admin/profile/${currentUser.id}`, profileData);
      setCurrentUser(response.data);
      
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.detail || 'Error updating profile');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    try {
      await http.post(`/admin/change-password`, {
        user_id: currentUser.id,
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      alert('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password');
    }
  };

  const generateJobStructuredData = (job) => {
    return {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      "title": job.title,
      "description": job.description,
      "identifier": {
        "@type": "PropertyValue",
        "name": "Tripod Technologies",
        "value": job.job_id
      },
      "datePosted": job.posted_date,
      "employmentType": job.type.toUpperCase().replace('-', '_'),
      "hiringOrganization": {
        "@type": "Organization",
        "name": "Tripod Technologies Private Limited",
        "sameAs": "https://tripodtechnologies.com"
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": job.location,
          "addressCountry": job.country
        }
      }
    };
  };

  // Login form
  if (!isAuthenticated) {
    if (!authChecked) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-cyan-50/30 flex items-center justify-center p-4">
          <div className="text-gray-700">Checking session…</div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/80 to-cyan-900/60 flex items-center justify-center p-4 relative overflow-hidden" style={{ minHeight: '100vh', height: '100vh' }}>
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.3),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(120,119,198,0.3),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_40%,rgba(59,130,246,0.2),transparent_40%)]"></div>
        
        <Card className="w-full max-w-md bg-white shadow-2xl relative z-10 border border-gray-200" style={{ maxHeight: '95vh', overflowY: 'auto' }}>
          <CardHeader className="text-center pb-6 pt-8">
            {/* Tripod Technologies Logo */}
            <div className="mx-auto mb-6 w-48 h-16 flex items-center justify-center">
              <img src="/tripod-logo.png" alt="Tripod Technologies" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-3xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">
              Tripod Technologies
            </CardTitle>
            <CardTitle className="text-xl font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-3">
              Private Limited
            </CardTitle>
            <CardDescription className="text-gray-900 text-xl leading-relaxed font-bold">
              Welcome to the Admin Panel
            </CardDescription>
            <p className="text-lg text-gray-700 mt-3 px-4 leading-relaxed">
              This panel is exclusively for employees. Please login to continue.
            </p>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1">
                <label className="block text-base font-semibold text-gray-900 mb-2">Username</label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  placeholder="Username"
                  required
                  aria-label="Username for admin login"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-base font-semibold text-gray-900 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    value={loginData.password}
                    onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200 bg-gray-50 focus:bg-white pr-10 text-sm"
                    placeholder="Password"
                    required
                    aria-label="Password for admin login"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-purple-600 transition-colors duration-200"
                    aria-label={showLoginPassword ? "Hide password" : "Show password"}
                  >
                    {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              {loginError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-600 font-medium">{loginError}</p>
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white font-semibold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-sm"
                aria-label="Login to admin panel"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing In...
                  </div>
                ) : (
                  'Sign In to Dashboard'
                )}
              </Button>
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordOpen(true)}
                  className="text-sm text-purple-600 hover:text-purple-800 hover:underline transition-colors duration-200 font-medium"
                  aria-label="Forgot password? Click to reset"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
            <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200/50 rounded-lg">
              <p className="text-sm text-gray-700 text-center leading-relaxed font-medium">
                🔐 <span className="font-bold text-purple-700">Secure Access Only</span><br/>
                This system is monitored for unauthorized access attempts.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Forgot Password Modal */}
        {isForgotPasswordOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md bg-white">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Reset Password</CardTitle>
                  <Button
                    onClick={() => setIsForgotPasswordOpen(false)}
                    variant="ghost"
                    size="sm"
                    aria-label="Close reset password dialog"
                    title="Close reset password dialog"
                    className="min-w-[44px] min-h-[44px]"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <CardDescription>
                  Enter your email to receive a password reset link
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label htmlFor="forgot_email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      id="forgot_email"
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Send Reset Link
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reset Password Modal (Simulated Link Click) */}
        {isResetPasswordOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md bg-white">
              <CardHeader>
                <CardTitle>Set New Password</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label htmlFor="reset_password" className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <div className="relative">
                      <input
                        id="reset_password"
                        type={showResetPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-2 min-w-[44px] min-h-[44px]"
                        aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                        title={showResetPassword ? 'Hide password' : 'Show password'}
                      >
                        {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="reset_password_confirm" className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                    <div className="relative">
                      <input
                        id="reset_password_confirm"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-2 min-w-[44px] min-h-[44px]"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        title={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {passwordResetStatus && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{passwordResetStatus}</p>
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Update Password
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/40 to-cyan-50/30 p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(147,51,234,0.05),transparent_40%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_80%,rgba(6,182,212,0.05),transparent_40%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(168,85,247,0.03),transparent_30%)]"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            {/* Tripod Technologies Logo */}
            <div className="w-[304px] h-[152px] flex items-center justify-center">
              <img src="/tripod-logo.png" alt="Tripod Technologies" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">
                Tripod Technologies
              </h1>
              <h2 className="text-2xl font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-3">
                Admin Dashboard
              </h2>
              <p className="text-gray-600 text-lg">Empowering innovation through intelligent management</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {currentUser && (
              <div className="text-right mr-4 px-6 py-3 bg-gradient-to-r from-white to-purple-50 rounded-2xl shadow-lg border border-purple-100">
                <p className="text-lg font-bold text-gray-800">{currentUser.name}</p>
                <p className="text-sm text-purple-600 font-medium">{currentUser.role}</p>
              </div>
            )}
            <Button
              onClick={() => {
                http.post('/admin/logout').catch(() => undefined);
                setIsAuthenticated(false);
                setCurrentUser(null);
              }}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 font-semibold px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
            >
              <Lock className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Main Layout with Sidebar */}
        <div className="flex gap-6">
          {/* Sidebar with Tabs */}
          <div className="w-64 bg-white/95 backdrop-blur-sm border border-cyan-200/50 rounded-2xl p-4 shadow-lg">
            <div className="space-y-2">
              {/* User Info */}
              {currentUser && (
                <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-xl border border-purple-100">
                  <p className="text-sm font-semibold text-gray-800">{currentUser.name}</p>
                  <p className="text-xs text-purple-600">{currentUser.role}</p>
                  <Button
                    onClick={() => setActiveTab('profile')}
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full text-xs"
                  >
                    <Settings className="w-3 h-3 mr-1" />
                    Settings
                  </Button>
                </div>
              )}
              
              {/* Navigation Tabs */}
              <Button
                onClick={() => setActiveTab('jobs')}
                className={activeTab === 'jobs' 
                  ? 'w-full justify-start bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-lg' 
                  : 'w-full justify-start bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200'}
              >
                <Users className="w-4 h-4 mr-2" />
                Jobs Management
              </Button>
              
              <Button
                onClick={() => setActiveTab('typography')}
                className={activeTab === 'typography' 
                  ? 'w-full justify-start bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-lg' 
                  : 'w-full justify-start bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200'}
              >
                <Type className="w-4 h-4 mr-2" />
                Typography
              </Button>
              
              {hasModule('users') && (
                <Button
                  onClick={() => setActiveTab('users')}
                  className={activeTab === 'users' 
                    ? 'w-full justify-start bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-lg' 
                    : 'w-full justify-start bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200'}
                >
                  <Users className="w-4 h-4 mr-2" />
                  User Management
                </Button>
              )}
              
              {hasModule('landing_pages') && (
                <Button
                  onClick={() => setActiveTab('landing_pages')}
                  className={activeTab === 'landing_pages' 
                    ? 'w-full justify-start bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-lg' 
                    : 'w-full justify-start bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200'}
                >
                  <Layout className="w-4 h-4 mr-2" />
                  Landing Pages
                </Button>
              )}
              
              {hasModule('leads') && (
                <Button
                  onClick={() => {
                    setActiveTab('leads');
                    fetchLeads();
                  }}
                  className={activeTab === 'leads' 
                    ? 'w-full justify-start bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-lg' 
                    : 'w-full justify-start bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200'}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Leads
                </Button>
              )}
              
              {hasModule('logs') && (
                <Button
                  onClick={() => {
                    setActiveTab('logs');
                    fetchAdminLogs();
                  }}
                  className={activeTab === 'logs' 
                    ? 'w-full justify-start bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-lg' 
                    : 'w-full justify-start bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200'}
                >
                  <Activity className="w-4 h-4 mr-2" />
                  Activity Logs
                </Button>
              )}
              
              {currentUser?.role === 'Super Admin' && (
                <Button
                  onClick={() => {
                    setActiveTab('smtp');
                    fetchSmtpSettings();
                  }}
                  className={activeTab === 'smtp' 
                    ? 'w-full justify-start bg-gradient-to-r from-purple-600 to-cyan-500 text-white shadow-lg' 
                    : 'w-full justify-start bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200'}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  SMTP Settings
                </Button>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">


          {activeTab === 'smtp' && (
            <Card className="bg-white shadow-xl border-0">
            <CardHeader>
              <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                SMTP Settings
              </CardTitle>
              <CardDescription>
                Updates are saved on the server and used for all outgoing emails.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSmtpSave} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Server</label>
                    <input
                      type="text"
                      value={smtpSettings.server}
                      onChange={(e) => setSmtpSettings(prev => ({...prev, server: e.target.value}))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                    <input
                      type="number"
                      value={smtpSettings.port}
                      onChange={(e) => setSmtpSettings(prev => ({...prev, port: e.target.value}))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sender Email</label>
                    <input
                      type="email"
                      value={smtpSettings.sender_email}
                      onChange={(e) => setSmtpSettings(prev => ({...prev, sender_email: e.target.value}))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sender Password</label>
                    <input
                      type="password"
                      value={smtpSettings.password}
                      onChange={(e) => setSmtpSettings(prev => ({...prev, password: e.target.value}))}
                      placeholder={smtpSettings.has_password ? 'Saved (leave blank to keep)' : 'Enter password'}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recipients (comma-separated)</label>
                  <input
                    type="text"
                    value={smtpSettings.recipients}
                    onChange={(e) => setSmtpSettings(prev => ({...prev, recipients: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                    required
                  />
                </div>

                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!smtpSettings.use_tls}
                    onChange={(e) => setSmtpSettings(prev => ({...prev, use_tls: e.target.checked}))}
                  />
                  <span>Use TLS (STARTTLS)</span>
                </label>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                    aria-label="Send password reset email"
                  >
                    Save SMTP Settings
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
          <>
            {isViewingUser ? (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                <div className="text-amber-900">
                  Viewing jobs created by <span className="font-medium">{viewUser?.name || viewUser?.email}</span> (read-only)
                </div>
                <Button
                  onClick={() => setViewUser(null)}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  aria-label="Exit user view mode"
                >
                  Exit View
                </Button>
              </div>
            ) : (
              <div className="flex justify-end mb-6">
                <Button
                  onClick={() => setShowForm(true)}
                  className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Job
                </Button>
              </div>
            )}

            {/* Job Form Modal */}
            {showForm && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl">
                  <CardHeader className="sticky top-0 bg-white/80 backdrop-blur-xl z-10 border-b border-gray-100">
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-2xl text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                          {editingJob ? 'Edit Job Posting' : 'Create New Job Posting'}
                        </CardTitle>
                        <CardDescription className="text-base">
                          Posted by: {currentUser?.name} ({currentUser?.email})
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => {
                          setShowForm(false);
                          setEditingJob(null);
                        }}
                        variant="ghost"
                        size="sm"
                        className="min-w-[44px] min-h-[44px] rounded-full hover:bg-gray-100"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <CardDescription>
                      Posted by: {currentUser?.name} ({currentUser?.email})
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
                          <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Experience *</label>
                          <input
                            type="text"
                            name="experience"
                            value={formData.experience}
                            onChange={handleInputChange}
                            placeholder="e.g., 3-5 years"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Salary Range *</label>
                          <input
                            type="text"
                            name="salary"
                            value={formData.salary}
                            onChange={handleInputChange}
                            placeholder="e.g., $50,000 - $80,000"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
                          <select
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          >
                            <option value="Full-time">Full-time</option>
                            <option value="Part-time">Part-time</option>
                            <option value="Contract">Contract</option>
                            <option value="Internship">Internship</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                          <input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            placeholder="e.g., Nagpur, Maharashtra"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                          <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleInputChange}
                            placeholder="e.g., India"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="remote"
                            checked={formData.remote}
                            onChange={handleInputChange}
                            className="rounded border-gray-300 focus:ring-cyan-400"
                          />
                          <span className="text-sm text-gray-700">Remote work available</span>
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Job Description *</label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 resize-none"
                          placeholder="Detailed job description..."
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Requirements (one per line) *
                        </label>
                        <textarea
                          value={formData.requirements.join('\n')}
                          onChange={handleRequirementsChange}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 resize-none"
                          placeholder="React.js, Node.js&#10;5+ years experience&#10;Strong communication skills"
                          required
                        />
                      </div>

                      <div className="flex justify-end space-x-4 pt-4">
                        <Button
                          type="button"
                          onClick={() => {
                            setShowForm(false);
                            setEditingJob(null);
                          }}
                          variant="outline"
                          aria-label="Cancel job form"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                          aria-label={editingJob ? 'Update job posting' : 'Create new job posting'}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {editingJob ? 'Update Job' : 'Create Job'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Jobs List */}
            <div className="space-y-6">
              {jobsForDisplay.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <p className="text-gray-500 mb-4">No jobs found</p>
                    {!isViewingUser && (
                      <Button
                        onClick={() => setShowForm(true)}
                        className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                      >
                        Add Your First Job
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                jobsForDisplay.map((job) => (
                  <Card key={job.id} className="bg-white/95 backdrop-blur-sm border border-cyan-200/50">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-mono rounded-full">
                              {job.job_id || `JOB-${job.id.slice(0, 8).toUpperCase()}`}
                            </span>
                            {!job.is_active && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Inactive</span>
                            )}
                          </div>
                          <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                            {job.title}
                          </CardTitle>
                          <CardDescription>
                            Posted on {new Date(job.posted_date).toLocaleDateString()}
                          </CardDescription>
                        </div>
                        <div className="flex space-x-2">
                          {canEditJob(job) && (
                            <Button
                              onClick={() => handleEdit(job)}
                              size="sm"
                              variant="outline"
                              className="border-blue-300 text-blue-600 hover:bg-blue-50 min-w-[44px] min-h-[44px]"
                              aria-label="Edit job"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {canDeleteJob(job) && (
                            <Button
                              onClick={() => handleDelete(job.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 min-w-[44px] min-h-[44px]"
                              aria-label="Delete job"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="w-4 h-4 text-purple-500 mr-2" />
                          <span>{job.experience}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <DollarSign className="w-4 h-4 text-cyan-500 mr-2" />
                          <span>{job.salary}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-purple-500 mr-2" />
                          <span>{job.location}, {job.country}</span>
                          {job.remote && (
                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                              Remote
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4">{job.description}</p>
                      
                      {/* Recruiter Info */}
                      {job.recruiter && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-lg border border-purple-200">
                          <h4 className="text-sm font-semibold text-purple-700 mb-2">Posted By:</h4>
                          <div className="grid md:grid-cols-3 gap-2 text-sm">
                            <div className="flex items-center text-gray-600">
                              <User className="w-4 h-4 text-purple-500 mr-2" />
                              <span>{job.recruiter.name}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Mail className="w-4 h-4 text-cyan-500 mr-2" />
                              <span>{job.recruiter.email}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Phone className="w-4 h-4 text-purple-500 mr-2" />
                              <span>{job.recruiter.phone}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* SEO Structured Data Preview */}
                      <details className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <summary className="cursor-pointer text-sm font-medium text-gray-700">
                          View Google Job Posting Schema (SEO)
                        </summary>
                        <pre className="mt-2 text-xs text-gray-600 overflow-x-auto">
                          {JSON.stringify(generateJobStructuredData(job), null, 2)}
                        </pre>
                      </details>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        )}

          {/* Users Tab */}
          {activeTab === 'users' && hasModule('users') && (
          <>
            <div className="flex justify-end mb-6">
              <Button
                onClick={() => setShowUserForm(true)}
                className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                aria-label="Add new user account"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New User
              </Button>
            </div>

            {/* User Form Modal */}
            {showUserForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto">
                <Card className="w-full max-w-md bg-white my-6 max-h-[90vh] overflow-y-auto">
                  <CardHeader className="sticky top-0 bg-white z-10">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                        {editingUser ? 'Edit User' : 'Add New User'}
                      </CardTitle>
                      <Button
                          onClick={() => {
                            setShowUserForm(false);
                            setEditingUser(null);
                            setUserFormData({
                              username: '',
                              password: '',
                              name: '',
                              email: '',
                              secondary_email: '',
                              phone: '',
                              role: 'Recruiter',
                              modules: ['jobs'],
                              is_active: true
                            });
                          }}
                          variant="ghost"
                          size="sm"
                          aria-label="Close user dialog"
                          title="Close user dialog"
                          className="min-w-[44px] min-h-[44px]"
                        >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleUserSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="user_full_name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                        <input
                          id="user_full_name"
                          type="text"
                          name="name"
                          value={userFormData.name}
                          onChange={handleUserInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="user_username" className="block text-sm font-medium text-gray-700 mb-2">Username *</label>
                        <input
                          id="user_username"
                          type="text"
                          name="username"
                          value={userFormData.username}
                          onChange={handleUserInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password {editingUser ? '(leave blank to keep current)' : '*'}
                        </label>
                        <div className="relative">
                          <input
                            id="user_password"
                            type={showUserPassword ? "text" : "password"}
                            name="password"
                            value={userFormData.password}
                            onChange={handleUserInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 pr-10"
                            required={!editingUser}
                          />
                          <button
                            type="button"
                            onClick={() => setShowUserPassword(!showUserPassword)}
                            className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-2 min-w-[44px] min-h-[44px]"
                            aria-label={showUserPassword ? 'Hide password' : 'Show password'}
                            title={showUserPassword ? 'Hide password' : 'Show password'}
                          >
                            {showUserPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="user_email" className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          id="user_email"
                          type="email"
                          name="email"
                          value={userFormData.email}
                          onChange={handleUserInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="user_secondary_email" className="block text-sm font-medium text-gray-700 mb-2">Secondary Email</label>
                        <input
                          id="user_secondary_email"
                          type="email"
                          name="secondary_email"
                          value={userFormData.secondary_email}
                          onChange={handleUserInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          placeholder="personal@example.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="user_phone" className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                        <input
                          id="user_phone"
                          type="tel"
                          name="phone"
                          value={userFormData.phone}
                          onChange={handleUserInputChange}
                          placeholder="+91-XXXXX XXXXX"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="user_role" className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                        <select
                          id="user_role"
                          name="role"
                          value={userFormData.role}
                          onChange={handleUserInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        >
                          <option value="Recruiter">Recruiter</option>
                          <option value="HR Manager">HR Manager</option>
                          <option value="Tech Lead">Tech Lead</option>
                          <option value="Super Admin">Super Admin</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Module Access</label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex items-center space-x-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={userFormData.modules?.includes('jobs')}
                              onChange={() => handleUserModuleToggle('jobs')}
                            />
                            <span>Jobs</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={userFormData.modules?.includes('landing_pages')}
                              onChange={() => handleUserModuleToggle('landing_pages')}
                            />
                            <span>Landing Pages</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={userFormData.modules?.includes('users')}
                              onChange={() => handleUserModuleToggle('users')}
                            />
                            <span>User Management</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={userFormData.modules?.includes('leads')}
                              onChange={() => handleUserModuleToggle('leads')}
                            />
                            <span>Leads</span>
                          </label>
                          <label className="flex items-center space-x-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={userFormData.modules?.includes('logs')}
                              onChange={() => handleUserModuleToggle('logs')}
                            />
                            <span>Activity Logs</span>
                          </label>
                        </div>
                      </div>
                      <label className="flex items-center space-x-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={userFormData.is_active !== false}
                          onChange={handleUserInputChange}
                        />
                        <span>Active user</span>
                      </label>
                      <div className="flex justify-end space-x-4 pt-4">
                        <Button
                          type="button"
                          onClick={() => {
                            setShowUserForm(false);
                            setEditingUser(null);
                          }}
                          variant="outline"
                          aria-label="Cancel user form"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                          aria-label={editingUser ? 'Update user account' : 'Create new user account'}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {editingUser ? 'Update User' : 'Create User'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Users List */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {users.length === 0 ? (
                <Card className="col-span-full text-center py-12">
                  <CardContent>
                    <p className="text-gray-500 mb-4">No users created yet</p>
                    <Button
                        onClick={() => setShowUserForm(true)}
                        className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                        aria-label="Create first user account"
                      >
                      Add First User
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                users.map((user) => (
                  <Card key={user.id} className="bg-white/95 backdrop-blur-sm border border-cyan-200/50">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{user.name}</h3>
                            <p className="text-sm text-purple-600">{user.role}</p>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          {isSuperAdmin && (
                            <Button
                              onClick={() => {
                                setViewUser(user);
                                setActiveTab('jobs');
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-amber-700 hover:bg-amber-50"
                            >
                              View
                            </Button>
                          )}
                          <Button
                            onClick={() => handleEditUser(user)}
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:bg-blue-50 min-w-[44px] min-h-[44px]"
                            aria-label="Edit user"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                                onClick={() => {
                                  const superAdminCount = users.filter(u => u.role === 'Super Admin').length;
                                  if (user.id === currentUser?.id) {
                                    alert('You cannot delete your own user');
                                    return;
                                  }
                                  if (user.role === 'Super Admin' && superAdminCount <= 1) {
                                    alert('Cannot delete the last Super Admin user');
                                    return;
                                  }
                                  handleDeleteUser(user.id);
                                }}
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 min-w-[44px] min-h-[44px]"
                                aria-label={`Delete user ${user.name || user.username}`}
                                title="Delete user"
                              >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-600">
                          <User className="w-4 h-4 text-purple-500 mr-2" />
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{user.username}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Mail className="w-4 h-4 text-cyan-500 mr-2" />
                          <span>{user.email}</span>
                        </div>
                        {user.secondary_email && (
                          <div className="flex items-center text-gray-600">
                            <Mail className="w-4 h-4 text-cyan-500 mr-2" />
                            <span>{user.secondary_email}</span>
                          </div>
                        )}
                        <div className="flex items-center text-gray-600">
                          <Phone className="w-4 h-4 text-purple-500 mr-2" />
                          <span>{user.phone}</span>
                        </div>
                        {Array.isArray(user.modules) && user.modules.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {user.modules.map((m) => (
                              <span
                                key={m}
                                className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700"
                              >
                                {m}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        )}

          {/* Landing Pages Tab */}
          {activeTab === 'landing_pages' && hasModule('landing_pages') && (
          <>
            {isViewingUser ? (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                <div className="text-amber-900">
                  Viewing landing pages created by <span className="font-medium">{viewUser?.name || viewUser?.email}</span> (read-only)
                </div>
                <Button
                  onClick={() => setViewUser(null)}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                >
                  Exit View
                </Button>
              </div>
            ) : (
              <div className="flex justify-end mb-6">
                <Button
                  onClick={() => setShowLandingPageForm(true)}
                  className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Landing Page
                </Button>
              </div>
            )}

            {/* Landing Page Form Modal */}
            {showLandingPageForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                        {editingLandingPage ? 'Edit Landing Page' : 'Create New Landing Page'}
                      </CardTitle>
                      <Button
                        onClick={() => {
                          setShowLandingPageForm(false);
                          setEditingLandingPage(null);
                          setShowHtmlPreview(false);
                          setLandingPageFormData({
                            slug: '',
                            title: '',
                            html_content: '',
                            is_active: true,
                            valid_till: ''
                          });
                        }}
                        variant="ghost"
                        size="sm"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleLandingPageSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Page Title *</label>
                          <input
                            type="text"
                            name="title"
                            value={landingPageFormData.title}
                            onChange={handleLandingPageInputChange}
                            placeholder="e.g. Summer Sale Promotion"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">URL Slug *</label>
                          <div className="flex">
                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                              .../
                            </span>
                            <input
                              type="text"
                              name="slug"
                              value={landingPageFormData.slug}
                              onChange={handleLandingPageInputChange}
                              placeholder="summer-sale"
                              className="w-full px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-cyan-400"
                              required
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Final URL: www.tripodtechnologies.com/{landingPageFormData.slug || 'your-slug'}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description / Purpose</label>
                        <textarea
                          name="description"
                          value={landingPageFormData.description}
                          onChange={handleLandingPageInputChange}
                          rows={2}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 resize-none"
                          placeholder="Internal note: What is this landing page used for?"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Valid Until (Optional)</label>
                        <input
                          type="date"
                          name="valid_till"
                          value={landingPageFormData.valid_till}
                          onChange={handleLandingPageInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Leave empty for no expiration. Page will automatically become inactive after this date.
                        </p>
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-sm font-medium text-gray-700">HTML Content *</label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          >
                            {showHtmlPreview ? <Code className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                            {showHtmlPreview ? 'Edit Code' : 'Preview'}
                          </Button>
                        </div>
                        
                        {showHtmlPreview ? (
                          <div className="w-full h-96 border border-gray-300 rounded-lg overflow-auto p-4 bg-white">
                            <iframe
                              srcDoc={landingPageFormData.html_content}
                              title="Preview"
                              className="w-full h-full border-0"
                            />
                          </div>
                        ) : (
                          <>
                            <textarea
                              name="html_content"
                              value={landingPageFormData.html_content}
                              onChange={handleLandingPageInputChange}
                              rows={15}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 font-mono text-sm mb-2"
                              placeholder="Paste your HTML code here..."
                              required
                            />
                            <div className="flex flex-col w-full gap-4">
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <label className="block text-sm text-gray-700 mb-1">Landing Page URL</label>
                                  <input
                                    type="text"
                                    value={importUrl}
                                    onChange={(e) => setImportUrl(e.target.value)}
                                    placeholder="Paste landing page URL here (e.g., https://example.com)"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                                    aria-label="Landing page URL to import"
                                  />
                                </div>
                                <Button
                                  type="button"
                                  onClick={handleLandingPageUrlUpload}
                                  className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap self-end"
                                  disabled={loading}
                                  aria-label="Import landing page from URL"
                                >
                                  {loading ? 'Fetching...' : 'Import from URL'}
                                </Button>
                              </div>

                              <div className="flex items-center justify-center w-full">
                                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 border-gray-300 hover:border-purple-500 transition-colors">
                                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <Upload className="w-6 h-6 mb-2 text-gray-400" />
                                    <p className="text-sm text-gray-500">
                                      <span className="font-semibold">Or upload HTML file</span>
                                    </p>
                                  </div>
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".html,.htm"
                                    onChange={handleLandingPageFileUpload}
                                    aria-label="Upload HTML file for landing page"
                                  />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 text-center">Upload .html or .htm files</p>
                            </div>
                          </>
                        )}
                      </div>

                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={landingPageFormData.is_active}
                            onChange={handleLandingPageInputChange}
                            className="rounded border-gray-300 focus:ring-cyan-400"
                          />
                          <span className="text-sm text-gray-700">Active (Publicly accessible)</span>
                        </label>
                      </div>

                      <div className="flex justify-end space-x-4 pt-4 border-t">
                        <Button
                          type="button"
                          onClick={() => {
                            setShowLandingPageForm(false);
                            setEditingLandingPage(null);
                          }}
                          variant="outline"
                          aria-label="Cancel landing page form"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                          aria-label={editingLandingPage ? 'Update landing page' : 'Create new landing page'}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {editingLandingPage ? 'Update Page' : 'Create Page'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Landing Pages List */}
            <div className="space-y-6">
              {landingPagesForDisplay.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <p className="text-gray-500 mb-4">No landing pages found</p>
                    {!isViewingUser && (
                        <Button
                          onClick={() => setShowLandingPageForm(true)}
                          className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                          aria-label="Create first landing page"
                        >
                        Create First Landing Page
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                landingPagesForDisplay.map((page) => {
                  const isExpired = page.valid_till && new Date(page.valid_till) < new Date();
                  const shouldBeInactive = isExpired && page.is_active;
                  
                  return (
                    <Card key={page.id} className={`bg-white/95 backdrop-blur-sm border ${isExpired ? 'border-red-200' : 'border-cyan-200/50'}`}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {editingLandingPageName === page.id ? (
                                <div className="flex items-center gap-2 flex-1">
                                  <input
                                    type="text"
                                    value={editingNameValue}
                                    onChange={(e) => setEditingNameValue(e.target.value)}
                                    className="px-2 py-1 border border-gray-300 rounded text-lg font-semibold"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') saveLandingPageName(page);
                                      if (e.key === 'Escape') cancelEditingName();
                                    }}
                                    autoFocus
                                  />
                                  <Button
                                  size="sm"
                                  onClick={() => saveLandingPageName(page)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  aria-label="Save page name changes"
                                >
                                    Save
                                  </Button>
                                  <Button
                                  size="sm"
                                  onClick={cancelEditingName}
                                  variant="outline"
                                  aria-label="Cancel page name editing"
                                >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <h3 
                                    className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-purple-600"
                                    onClick={() => startEditingName(page)}
                                    title="Click to edit name"
                                  >
                                    {page.title}
                                  </h3>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => startEditingName(page)}
                                    className="text-gray-400 hover:text-purple-600"
                                    aria-label="Edit page name"
                                    title="Edit page name"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                </>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                                /{page.slug}
                              </span>
                              
                              <Button
                                size="sm"
                                onClick={() => toggleLandingPageStatus(page)}
                                className={`${
                                  page.is_active 
                                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                                } border-0`}
                                aria-label={page.is_active ? 'Pause landing page' : 'Activate landing page'}
                                title={page.is_active ? 'Click to pause page' : 'Click to activate page'}
                              >
                                {page.is_active ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                                {page.is_active ? 'Active' : 'Paused'}
                              </Button>
                              
                              {isExpired && (
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  Expired
                                </span>
                              )}
                              
                              {shouldBeInactive && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                  Should be inactive
                                </span>
                              )}
                            </div>
                            
                            {page.valid_till && (
                              <div className="text-xs text-gray-500 mb-2">
                                Valid until: {new Date(page.valid_till).toLocaleDateString()}
                                {isExpired && ' (Expired)'}
                              </div>
                            )}
                            
                            <div className="flex items-center space-x-4 text-sm">
                              <a 
                                href={`${window.location.origin}/${page.slug}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center text-blue-600 hover:underline"
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                View Page
                              </a>
                              <button
                                onClick={() => copyToClipboard(`${window.location.origin}/${page.slug}`)}
                                className="flex items-center text-gray-500 hover:text-gray-700"
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy Link
                              </button>
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            {!isViewingUser && (isSuperAdmin || String(page?.created_by_email || '').toLowerCase() === currentUserEmail) && (
                              <Button
                                onClick={() => handleEditLandingPage(page)}
                                size="sm"
                                variant="outline"
                                className="border-blue-300 text-blue-600 hover:bg-blue-50 min-w-[44px] min-h-[44px]"
                                aria-label={`Edit landing page ${page.title}`}
                                title="Edit landing page"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {!isViewingUser && isSuperAdmin && (
                              <Button
                                onClick={() => handleDeleteLandingPage(page.id)}
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50 min-w-[44px] min-h-[44px]"
                                aria-label={`Delete landing page ${page.title}`}
                                title="Delete landing page"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 text-xs text-gray-400">
                          Created on {new Date(page.created_at).toLocaleDateString()}
                          {page.created_by_email && (
                            <span className="ml-2 text-gray-500">
                              by {page.created_by_email.split('@')[0]}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Leads Tab */}
          {activeTab === 'leads' && hasModule('leads') && (
          <>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-700">Source</label>
                  <select
                    value={leadFilters.source}
                    onChange={(e) => setLeadFilters(prev => ({ ...prev, source: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="all">All</option>
                    <option value="landing">Landing Pages</option>
                    <option value="contact">Contact Forms</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-700">Interested</label>
                  <select
                    value={leadFilters.interested}
                    onChange={(e) => setLeadFilters(prev => ({ ...prev, interested: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="all">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
                
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={leadFilters.openOnly}
                    onChange={(e) => setLeadFilters(prev => ({ ...prev, openOnly: e.target.checked }))}
                  />
                  Open only
                </label>
              </div>
              
              <div className="flex items-center gap-2">
                {selectedLeads.size > 0 && (
                  <Button
                    onClick={handleBulkClose}
                    variant="outline"
                    className="border-orange-300 text-orange-600 hover:bg-orange-50"
                    aria-label={`Close ${selectedLeads.size} selected leads`}
                  >
                    <Archive className="w-4 h-4 mr-1" />
                    Close ({selectedLeads.size})
                  </Button>
                )}
                <Button
                  onClick={exportToExcel}
                  variant="outline"
                  className="border-green-300 text-green-600 hover:bg-green-50"
                  aria-label="Export leads to Excel"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Export
                </Button>
                <Button
                  onClick={fetchLeads}
                  variant="outline"
                  className="border-gray-300"
                  aria-label="Refresh leads data"
                >
                  Refresh
                </Button>
              </div>
            </div>
            
            {/* Advanced Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Search by name, email, company..."
                  value={leadFilters.search}
                  onChange={(e) => setLeadFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={leadFilters.dateFrom}
                  onChange={(e) => setLeadFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={leadFilters.dateTo}
                  onChange={(e) => setLeadFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {showLeadForm && editingLead && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
                  <CardHeader className="sticky top-0 bg-white z-10 border-b">
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                          Edit Lead
                        </CardTitle>
                        <CardDescription>
                          {editingLead.source}{editingLead.slug ? ` • ${editingLead.slug}` : ''}
                        </CardDescription>
                      </div>
                      <Button
                          onClick={() => {
                            setShowLeadForm(false);
                            setEditingLead(null);
                          }}
                          variant="ghost"
                          size="sm"
                          className="min-w-[44px] min-h-[44px]"
                          aria-label="Close lead editor"
                          title="Close lead editor"
                        >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="py-6">
                    <form onSubmit={handleLeadSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                          <input 
                            name="name" 
                            value={leadFormData.name} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="Lead name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Professional</label>
                          <input 
                            name="professional" 
                            value={leadFormData.professional} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="Job title or role"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input 
                            type="email"
                            name="email" 
                            value={leadFormData.email} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="email@example.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                          <input 
                            type="tel"
                            name="phone" 
                            value={leadFormData.phone} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="+1-555-0123"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                          <input 
                            name="location" 
                            value={leadFormData.location} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="City, Country"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                          <input 
                            name="company" 
                            value={leadFormData.company} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="Company name"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Best Time to Reach</label>
                          <input 
                            name="reach_time" 
                            value={leadFormData.reach_time} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="e.g., 2-4 PM"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Time Zone</label>
                          <input 
                            name="reach_timezone" 
                            value={leadFormData.reach_timezone} 
                            onChange={handleLeadInputChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                            placeholder="e.g., EST, PST"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Interest Level</label>
                        <select 
                          name="interested" 
                          value={leadFormData.interested === true ? 'yes' : leadFormData.interested === false ? 'no' : 'unknown'} 
                          onChange={handleLeadInputChange} 
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-cyan-400"
                        >
                          <option value="unknown">Unknown</option>
                          <option value="yes">Interested</option>
                          <option value="no">Not interested</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          name="is_open" 
                          checked={leadFormData.is_open} 
                          onChange={handleLeadInputChange} 
                          className="rounded border-gray-300 focus:ring-cyan-400"
                        />
                        <label className="text-sm font-medium text-gray-700">Keep case open</label>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Primary Notes</label>
                        <textarea 
                          name="comments_1" 
                          value={leadFormData.comments_1} 
                          onChange={handleLeadInputChange} 
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 resize-none"
                          rows={4}
                          placeholder="Main notes about this lead..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                        <textarea 
                          name="comments_2" 
                          value={leadFormData.comments_2} 
                          onChange={handleLeadInputChange} 
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 resize-none"
                          rows={2}
                          placeholder="Additional comments..."
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => { 
                            setShowLeadForm(false); 
                            setEditingLead(null); 
                          }}
                          aria-label="Cancel lead editing"
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                          aria-label="Save lead changes"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Save Changes
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-white/95 backdrop-blur-sm border border-cyan-200/50">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">Leads</CardTitle>
                    <CardDescription>Landing page + contact form leads</CardDescription>
                  </div>
                  <div className="text-sm text-gray-500">
                    {leads.filter(l => leadFilters.openOnly ? l.is_open !== false : true).length} leads
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {leadsLoading ? (
                  <p className="text-gray-500">Loading leads...</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-600 border-b">
                          <th className="py-2 px-2">
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              title="Select all visible leads"
                            />
                          </th>
                          <th className="py-2">Name</th>
                          <th className="py-2">Service</th>
                          <th className="py-2">Phone</th>
                          <th className="py-2">Email</th>
                          <th className="py-2">Company</th>
                          <th className="py-2">Location</th>
                          <th className="py-2">Reach</th>
                          <th className="py-2">Interested</th>
                          <th className="py-2">Status</th>
                          <th className="py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads
                          .filter(l => {
                            if (leadFilters.openOnly && l.is_open === false) return false;
                            
                            // Search filter
                            if (leadFilters.search) {
                              const searchTerm = leadFilters.search.toLowerCase();
                              const searchableFields = [
                                l.name, l.email, l.company, l.location, 
                                l.professional, l.service
                              ].filter(Boolean).join(' ').toLowerCase();
                              
                              if (!searchableFields.includes(searchTerm)) return false;
                            }
                            
                            // Interested filter
                            if (leadFilters.interested !== 'all') {
                              if (leadFilters.interested === 'yes' && l.interested !== true) return false;
                              if (leadFilters.interested === 'no' && l.interested !== false) return false;
                              if (leadFilters.interested === 'unknown' && l.interested !== null) return false;
                            }
                            
                            // Date filters
                            if (leadFilters.dateFrom || leadFilters.dateTo) {
                              const leadDate = new Date(l.created_at);
                              if (leadFilters.dateFrom && leadDate < new Date(leadFilters.dateFrom)) return false;
                              if (leadFilters.dateTo && leadDate > new Date(leadFilters.dateTo + 'T23:59:59')) return false;
                            }
                            
                            return true;
                          })
                          .map((lead) => (
                            <tr key={`${lead.source}-${lead.id}`} className="border-b last:border-b-0 hover:bg-gray-50">
                              <td className="py-2 px-2">
                                <input
                                  type="checkbox"
                                  checked={selectedLeads.has(`${lead.source}-${lead.id}`)}
                                  onChange={() => handleLeadSelect(`${lead.source}-${lead.id}`)}
                                  title="Select this lead"
                                />
                              </td>
                              <td className="py-3">
                                <div className="font-semibold text-gray-900 text-base">{lead.name || '-'}</div>
                                <div className="text-sm text-gray-600">{lead.source}{lead.slug ? ` • ${lead.slug}` : ''}</div>
                              </td>
                              <td className="py-2">{lead.service || '-'}</td>
                              <td className="py-3">
                                <div className="font-mono text-sm">{lead.phone || '-'}</div>
                              </td>
                              <td className="py-2">
                                <div className="font-mono text-xs max-w-32 truncate" title={lead.email}>
                                  {lead.email || '-'}
                                </div>
                              </td>
                              <td className="py-2">
                                <div className="max-w-24 truncate" title={lead.company}>
                                  {lead.company || '-'}
                                </div>
                              </td>
                              <td className="py-2">
                                <div className="max-w-24 truncate" title={lead.location}>
                                  {lead.location || '-'}
                                </div>
                              </td>
                              <td className="py-2">
                                <div className="text-xs">
                                  {lead.reach_time || '-'}
                                  {lead.reach_timezone && <div className="text-gray-500">({lead.reach_timezone})</div>}
                                </div>
                              </td>
                              <td className="py-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  lead.interested === true ? 'bg-green-100 text-green-800' :
                                  lead.interested === false ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {lead.interested === true ? 'Yes' : lead.interested === false ? 'No' : 'Unknown'}
                                </span>
                              </td>
                              <td className="py-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  lead.is_open === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {lead.is_open === false ? 'Closed' : 'Open'}
                                </span>
                              </td>
                              <td className="py-2">
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => openLeadEditor(lead)}
                                    className="min-w-[44px] min-h-[44px]"
                                    aria-label={`Edit lead ${lead.name}`}
                                    title="Edit lead details"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  {lead.is_open !== false && (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      onClick={() => updateLeadStatus(`${lead.source}-${lead.id}`, 'closed')}
                                      className="min-w-[44px] min-h-[44px] border-orange-300 text-orange-600 hover:bg-orange-50"
                                      aria-label={`Close lead ${lead.name}`}
                                      title="Close lead"
                                    >
                                      <Archive className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {lead.is_open === false && (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      onClick={() => updateLeadStatus(`${lead.source}-${lead.id}`, 'open')}
                                      className="min-w-[44px] min-h-[44px] border-green-300 text-green-600 hover:bg-green-50"
                                      aria-label={`Reopen lead ${lead.name}`}
                                      title="Reopen lead"
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

          {/* Typography Tab */}
          {activeTab === 'typography' && (
          <div className="space-y-6">
            <Card className="bg-white/95 backdrop-blur-sm border border-cyan-200/50">
              <CardHeader>
                <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                  Typography Style Guide
                </CardTitle>
                <CardDescription>
                  Preview of fonts and text styles used across the application.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Font Families */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Font Families</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">Sans Serif (Default)</p>
                      <div className="p-4 bg-gray-50 rounded-lg border font-sans">
                        <p className="text-xl">The quick brown fox jumps over the lazy dog.</p>
                        <p className="mt-2 text-sm text-gray-600">font-sans (Inter/System UI)</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">Monospace (Code)</p>
                      <div className="p-4 bg-gray-50 rounded-lg border font-mono">
                        <p className="text-xl">The quick brown fox jumps over the lazy dog.</p>
                        <p className="mt-2 text-sm text-gray-600">font-mono</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Headings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Headings</h3>
                  <div className="space-y-4">
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H1 / 4xl</span>
                      <h1 className="text-4xl font-bold">Tripod Technologies</h1>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H2 / 3xl</span>
                      <h2 className="text-3xl font-bold">Tripod Technologies</h2>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H3 / 2xl</span>
                      <h3 className="text-2xl font-bold">Tripod Technologies</h3>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H4 / xl</span>
                      <h4 className="text-xl font-bold">Tripod Technologies</h4>
                    </div>
                  </div>
                </div>

                {/* Body Text */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Body Text</h3>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Regular</p>
                        <p className="text-gray-900">
                          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Muted</p>
                        <p className="text-gray-500">
                          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Small</p>
                        <p className="text-sm text-gray-600">
                          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Gradient Text</p>
                        <p className="font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                          Tripod Technologies - Innovation First
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

          {/* Typography Tab */}
          {activeTab === 'typography' && (
          <div className="space-y-6">
            <Card className="bg-white/95 backdrop-blur-sm border border-cyan-200/50">
              <CardHeader>
                <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                  Typography Style Guide
                </CardTitle>
                <CardDescription>
                  Preview of fonts and text styles used across the application.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Font Families */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Font Families</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">Sans Serif (Default)</p>
                      <div className="p-4 bg-gray-50 rounded-lg border font-sans">
                        <p className="text-xl">The quick brown fox jumps over the lazy dog.</p>
                        <p className="mt-2 text-sm text-gray-600">font-sans (Inter/System UI)</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">Monospace (Code)</p>
                      <div className="p-4 bg-gray-50 rounded-lg border font-mono">
                        <p className="text-xl">The quick brown fox jumps over the lazy dog.</p>
                        <p className="mt-2 text-sm text-gray-600">font-mono</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Headings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Headings</h3>
                  <div className="space-y-4">
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H1 / 4xl</span>
                      <h1 className="text-4xl font-bold">Tripod Technologies</h1>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H2 / 3xl</span>
                      <h2 className="text-3xl font-bold">Tripod Technologies</h2>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H3 / 2xl</span>
                      <h3 className="text-2xl font-bold">Tripod Technologies</h3>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H4 / xl</span>
                      <h4 className="text-xl font-bold">Tripod Technologies</h4>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H5 / lg</span>
                      <h5 className="text-lg font-bold">Tripod Technologies</h5>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-gray-400 w-16">H6 / base</span>
                      <h6 className="text-base font-bold">Tripod Technologies</h6>
                    </div>
                  </div>
                </div>

                {/* Text Sizes */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Text Sizes</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">xs</span>
                      <p className="text-xs">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">sm</span>
                      <p className="text-sm">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">base</span>
                      <p className="text-base">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">lg</span>
                      <p className="text-lg">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">xl</span>
                      <p className="text-xl">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">2xl</span>
                      <p className="text-2xl">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                  </div>
                </div>

                {/* Font Weights */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Font Weights</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">light</span>
                      <p className="font-light">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">normal</span>
                      <p className="font-normal">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">medium</span>
                      <p className="font-medium">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">semibold</span>
                      <p className="font-semibold">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">bold</span>
                      <p className="font-bold">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400 w-16">extrabold</span>
                      <p className="font-extrabold">The quick brown fox jumps over the lazy dog.</p>
                    </div>
                  </div>
                </div>

                {/* Colors */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Brand Colors</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">Primary Text</p>
                      <div className="p-4 bg-gray-50 rounded-lg border">
                        <p className="font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                          Tripod Technologies - Innovation First
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">Secondary Text</p>
                      <div className="p-4 bg-gray-50 rounded-lg border">
                        <p className="font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                          Tripod Technologies - Innovation First
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="md:col-span-2 bg-white/95 backdrop-blur-sm border border-cyan-200/50">
              <CardHeader>
                <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                  Profile Information
                </CardTitle>
                <CardDescription>
                  Update your personal information and contact details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={profileData.username}
                          readOnly
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                          pattern="^[^@\s]+@tripodtechnologies\.com$"
                          title="Primary email must be a @tripodtechnologies.com address"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          value={profileData.secondary_email}
                          onChange={(e) => setProfileData({...profileData, secondary_email: e.target.value})}
                          placeholder="Backup email address"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-white/95 backdrop-blur-sm border border-cyan-200/50 h-fit">
              <CardHeader>
                <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                  Security
                </CardTitle>
                <CardDescription>
                  Update your password
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type={showProfilePassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowProfilePassword(!showProfilePassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showProfilePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white"
                  >
                    Update Password
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

          {/* Activity Logs Tab */}
          {activeTab === 'logs' && hasModule('logs') && (
          <Card className="bg-white/95 backdrop-blur-sm border border-cyan-200/50">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                    Activity Logs
                  </CardTitle>
                  <CardDescription>
                    Audit trail of all user activities
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  {currentUser?.role === 'Super Admin' && (
                    <select
                      value={logFilters.userId || ''}
                      onChange={(e) => setLogFilters(prev => ({ ...prev, userId: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    >
                      <option value="">All Users</option>
                      {users.map(user => (
                        <option key={user.id} value={user.email}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                  )}
                  <Button
                    onClick={fetchAdminLogs}
                    variant="outline"
                    className="border-gray-300"
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!!logsError && (
                <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                  {logsError}
                </div>
              )}
              {logsLoading ? (
                <p className="text-gray-500">Loading logs...</p>
              ) : adminLogs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No admin activity logs found</p>
                  <p className="text-sm text-gray-400">
                    Logs are generated when admins perform actions like creating, updating, or deleting items.
                  </p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {adminLogs.map((log, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            log.action === 'create' || log.action === 'job_created' || log.action === 'user_created' ? 'bg-green-100 text-green-800' :
                            log.action === 'update' || log.action === 'job_updated' || log.action === 'user_updated' ? 'bg-blue-100 text-blue-800' :
                            log.action === 'delete' || log.action === 'job_deleted' || log.action === 'user_deleted' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {log.action?.toUpperCase() || 'UNKNOWN'}
                          </span>
                          {!!log.resource_type && (
                            <span className="font-medium text-gray-900">
                              {log.resource_type}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatLogTimestamp(log.timestamp)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Admin:</span>
                          <span className="ml-2 font-medium">{log.admin_name || log.admin_email || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">User ID:</span>
                          <span className="ml-2 font-mono text-xs">{log.user_id || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">IP:</span>
                          <span className="ml-2 font-mono text-xs">{log.ip_address || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Resource ID:</span>
                          <span className="ml-2 font-mono text-xs">{log.resource_id || 'N/A'}</span>
                        </div>
                      </div>
                      
                      {log.details && (
                        <div className="mt-2 p-2 bg-white rounded border text-xs">
                          <span className="text-gray-600">Details:</span>
                          <pre className="mt-1 text-gray-700 whitespace-pre-wrap">
                            {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminJobs;
